import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { TableHead, TableWrapper } from '../components/TableWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { WithWrite } from '../lib/rbac';
import { formatContainerDisplayId } from '../lib/batch';
import { formatQty, isPieceUom } from '../lib/quantity';
import { purchaseTypeLabel } from '../lib/purchaseLabels';
import {
  purchaseDocumentsV2Api,
  type PurchaseDocumentV2Detail,
  type PurchaseDocumentV2FormData,
  type PurchaseDocumentsV2Meta,
} from '../services/purchaseDocumentsV2';

type FormRow = {
  rowId?: number;
  isBatchLocked?: boolean;
  containerId?: number | null;
  containerCode: string;
  productId: number | '';
  productSearch: string;
  qty: string;
  uomItemId: number | '';
  siteId: number | '';
  waterPercent: string;
  notes: string;
};

const EMPTY_FORM: PurchaseDocumentV2FormData = {
  purchaseType: 'HONEY',
  supplierId: null,
  documentDate: '',
  notes: '',
  rows: [],
};

const EMPTY_ROW: FormRow = {
  containerCode: '',
  productId: '',
  productSearch: '',
  qty: '',
  uomItemId: '',
  siteId: '',
  waterPercent: '',
  notes: '',
};

function formatDateOnly(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : '-';
}

function productOptionLabel(product: PurchaseDocumentsV2Meta['products'][number]): string {
  return product.sku ? `${product.sku} - ${product.name}` : product.name;
}

function isBulkHoneyOrPollen(meta: PurchaseDocumentsV2Meta, productId: number | ''): boolean {
  if (productId === '') return false;
  const product = meta.products.find((item) => item.id === Number(productId));
  const category = (product?.categoryLabel ?? '').trim().toLowerCase();
  return product?.kind === 'BULK' && (category === 'honung' || category === 'pollen');
}

function normalizeLocalizedDecimal(value: string): string {
  return value.replace(',', '.');
}

function purchaseTypeNeedsQualityFields(purchaseType: string): boolean {
  return purchaseType === 'HONEY' || purchaseType === 'POLLEN';
}

export function PurchaseDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { canRead } = useAuth();
  const documentId = Number(params.id);

  const [meta, setMeta] = useState<PurchaseDocumentsV2Meta | null>(null);
  const [selected, setSelected] = useState<PurchaseDocumentV2Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<PurchaseDocumentV2FormData>(EMPTY_FORM);
  const [rows, setRows] = useState<FormRow[]>([]);
  const [openProductSearchIndex, setOpenProductSearchIndex] = useState<number | null>(null);

  if (!canRead('receiving')) {
    return <Navigate to="/" replace />;
  }

  if (!Number.isFinite(documentId) || documentId <= 0) {
    return <Navigate to="/purchases" replace />;
  }

  const loadDocument = useCallback(async () => {
    setLoading(true);
    try {
      const [metaRes, detailRes] = await Promise.all([purchaseDocumentsV2Api.meta(), purchaseDocumentsV2Api.get(documentId)]);
      setMeta(metaRes);
      setSelected(detailRes);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda inköpsorder');
      navigate('/purchases', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [documentId, navigate, toast]);

  useEffect(() => {
    void loadDocument();
  }, [loadDocument]);

  const defaultSiteId = meta?.sites.find((site) => site.name === 'Lager')?.id ?? meta?.sites[0]?.id ?? '';

  const totalQty = useMemo(
    () => selected?.rows.reduce((sum, row) => sum + Number(row.qty ?? 0), 0) ?? 0,
    [selected]
  );

  const openEdit = () => {
    if (!selected || !meta) return;
    setForm({
      purchaseType: selected.purchaseType,
      supplierId: selected.supplierId,
      documentDate: selected.documentDate,
      notes: selected.notes ?? '',
      rows: selected.rows.map((row) => ({
        id: row.id ?? null,
        containerCode: row.containerCode ?? '',
        productId: row.productId,
        qty: Number(row.qty),
        uomItemId: row.uomItemId ?? null,
        siteId: row.siteId ?? null,
        waterPercent: row.waterPercent ?? null,
        notes: row.notes ?? null,
      })),
    });
    setRows(
      selected.rows.map((row) => {
        const product = meta.products.find((item) => item.id === row.productId);
        return {
        rowId: row.id,
        isBatchLocked: Boolean(row.isBatchLocked),
        containerId: row.containerId ?? null,
        containerCode: row.containerCode ?? '',
        productId: row.productId,
        productSearch: product ? productOptionLabel(product) : row.productName ?? '',
          qty: String(row.qty),
          uomItemId: row.uomItemId ?? '',
          siteId: row.siteId ?? defaultSiteId,
          waterPercent: row.waterPercent == null ? '' : String(row.waterPercent),
          notes: row.notes ?? '',
        };
      })
    );
    setOpenProductSearchIndex(null);
    setShowForm(true);
  };

  const updateRow = (index: number, patch: Partial<FormRow>) => {
    setRows((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
  };

  const handleProductSelect = (index: number, productId: number) => {
    const product = meta?.products.find((item) => item.id === productId);
    if (!product) return;
    updateRow(index, {
      productId,
      productSearch: productOptionLabel(product),
      uomItemId: product.defaultUomItemId ?? '',
      waterPercent: '',
    });
    setOpenProductSearchIndex(null);
  };

  const closeForm = () => {
    setShowForm(false);
    setForm(EMPTY_FORM);
    setRows([]);
    setOpenProductSearchIndex(null);
  };

  const save = async () => {
    if (!selected) return;

    const payload: PurchaseDocumentV2FormData = {
      ...form,
      rows: rows
        .filter((row) => row.productId !== '' && row.qty.trim() !== '')
        .map((row) => ({
          id: row.rowId ?? null,
          productId: Number(row.productId),
          containerCode: row.containerCode.trim() || null,
          qty: Number(normalizeLocalizedDecimal(row.qty)),
          uomItemId: row.uomItemId === '' ? null : Number(row.uomItemId),
          siteId: row.siteId === '' ? null : Number(row.siteId),
          waterPercent: row.waterPercent.trim() === '' ? null : Number(normalizeLocalizedDecimal(row.waterPercent)),
          notes: row.notes.trim() || null,
        })),
    };

    setSaving(true);
    try {
      await purchaseDocumentsV2Api.update(selected.id, payload);
      toast.success('Inköpsorder uppdaterad');
      closeForm();
      await loadDocument();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte spara inköpsorder');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !meta || !selected) {
    return <p className="text-sm text-gray-500">Laddar...</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <button
            type="button"
            onClick={() => navigate('/purchases')}
            className="mb-2 rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-100"
          >
            Tillbaka
          </button>
          <h1 className="text-lg font-semibold">Inköpsorder {selected.documentNumber}</h1>
          <p className="text-sm text-gray-600">{selected.supplierName ?? 'Ingen leverantör'}</p>
        </div>
        <WithWrite moduleCode="receiving">
          <button
            type="button"
            onClick={openEdit}
            className="rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-100"
          >
            Redigera
          </button>
        </WithWrite>
      </div>

      <div className="grid grid-cols-2 gap-3 rounded border border-gray-200 bg-white p-4 text-sm">
        <div>
          <div className="text-gray-500">Inköpsorder</div>
          <div className="font-medium">{selected.documentNumber}</div>
        </div>
        <div>
          <div className="text-gray-500">Typ</div>
          <div>{purchaseTypeLabel(selected.purchaseType, meta.purchaseTypes.find((item) => item.value === selected.purchaseType)?.label ?? null)}</div>
        </div>
        <div>
          <div className="text-gray-500">Leverantör</div>
          <div>{selected.supplierName ?? '-'}</div>
        </div>
        <div>
          <div className="text-gray-500">Datum</div>
          <div>{formatDateOnly(selected.documentDate)}</div>
        </div>
        <div>
          <div className="text-gray-500">Rader</div>
          <div>{selected.rows.length}</div>
        </div>
        <div>
          <div className="text-gray-500">Total mängd</div>
          <div>{formatQty(totalQty)}</div>
        </div>
        <div className="col-span-2">
          <div className="text-gray-500">Anteckningar</div>
          <div className="whitespace-pre-wrap">{selected.notes ?? '-'}</div>
        </div>
      </div>

      <div className="space-y-2">
        <h2 className="font-medium">Rader</h2>
        <p className="text-xs text-gray-500">Batcher som redan har använts i produktionen kan inte längre ändras eller tas bort.</p>
        <TableWrapper>
          <TableHead>
            <tr>
              <th className="px-2 py-1.5 text-left font-medium">Produkt</th>
              <th className="px-2 py-1.5 text-left font-medium">BatchNr</th>
              <th className="px-2 py-1.5 text-left font-medium">Mängd</th>
              <th className="px-2 py-1.5 text-left font-medium">Enhet</th>
              <th className="px-2 py-1.5 text-left font-medium">Lagerplats</th>
              <th className="px-2 py-1.5 text-left font-medium">Vattenhalt</th>
              <th className="px-2 py-1.5 text-left font-medium">Anteckningar</th>
            </tr>
          </TableHead>
          <tbody>
            {selected.rows.map((row) => (
              <tr key={row.id} className="border-b border-gray-100">
                <td className="px-2 py-1">{row.productName ?? '-'}</td>
                <td className="px-2 py-1">
                  <div className="font-medium">{formatContainerDisplayId(row.containerId, row.containerCode) || '-'}</div>
                </td>
                <td className="px-2 py-1">{formatQty(row.qty, row.uomLabel)}</td>
                <td className="px-2 py-1">{row.uomLabel ?? '-'}</td>
                <td className="px-2 py-1">{row.siteName ?? '-'}</td>
                <td className="px-2 py-1">{row.waterPercent == null ? '-' : `${row.waterPercent}%`}</td>
                <td className="px-2 py-1 whitespace-pre-wrap">{row.notes ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </TableWrapper>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded bg-white p-4 shadow-lg">
            <div className="space-y-3">
              <div>
                <h2 className="text-base font-semibold">Redigera inköpsorder</h2>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                <div>
                  <label className="block text-sm text-gray-600">Inköpsnummer</label>
                  <input value={selected.documentNumber} readOnly className="w-1/2 bg-gray-100 text-center text-lg font-semibold text-gray-700" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Typ *</label>
                  <select
                    value={form.purchaseType}
                    onChange={(event) => setForm((current) => ({ ...current, purchaseType: event.target.value }))}
                    className="w-full"
                  >
                    {meta.purchaseTypes.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Leverantör</label>
                  <select
                    value={form.supplierId ?? ''}
                    onChange={(event) => setForm((current) => ({ ...current, supplierId: event.target.value ? Number(event.target.value) : null }))}
                    className="w-full"
                  >
                    <option value="">-</option>
                    {meta.suppliers.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Datum *</label>
                  <input
                    type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD"
                    value={form.documentDate}
                    onChange={(event) => setForm((current) => ({ ...current, documentDate: event.target.value }))}
                    className="w-full"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-medium">Rader</h3>
                  <button
                    type="button"
                    onClick={() => setRows((current) => [...current, { ...EMPTY_ROW, siteId: defaultSiteId }])}
                    className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover"
                  >
                    Lägg till rad
                  </button>
                </div>
                <div className="overflow-hidden rounded border border-gray-200">
                  <div className="max-h-[52vh] overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-100 border-b border-gray-200">
                        <tr>
                          <th className="px-2 py-1.5 text-left font-medium">BatchNr</th>
                          <th className="px-2 py-1.5 text-left font-medium">Produkt</th>
                          <th className="px-2 py-1.5 text-left font-medium">Mängd</th>
                          <th className="px-2 py-1.5 text-left font-medium">Enhet</th>
                          <th className="px-2 py-1.5 text-left font-medium">Lagerplats</th>
                          <th className="px-2 py-1.5 text-left font-medium">Vattenhalt %</th>
                          <th className="px-2 py-1.5 text-left font-medium">Anteckningar</th>
                          <th className="px-2 py-1.5 text-left font-medium"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, index) => {
                          const rowLocked = Boolean(row.isBatchLocked);
                          const selectedUomLabel = meta.uoms.find((uom) => uom.id === (row.uomItemId === '' ? null : row.uomItemId))?.label ?? null;
                          const showBulkFields =
                            purchaseTypeNeedsQualityFields(form.purchaseType) || isBulkHoneyOrPollen(meta, row.productId);
                          const matchingProducts = meta.products
                            .filter((product) => {
                              const search = row.productSearch.trim().toLowerCase();
                              if (!search) return true;
                              return `${product.sku ?? ''} ${product.name}`.toLowerCase().includes(search);
                            })
                            .slice(0, 12);

                          return (
                            <tr key={row.rowId ?? index} className="border-b border-gray-100 align-top">
                              <td className="px-2 py-1.5 min-w-[90px] font-medium text-gray-700">
                                {formatContainerDisplayId(row.containerId, row.containerCode) || 'Skapas automatiskt'}
                              </td>
                              <td className="px-2 py-1.5 min-w-[320px]">
                                <div className="relative">
                                  <input
                                    value={row.productSearch}
                                    placeholder="Sök artikelnummer eller namn"
                                    onFocus={() => {
                                      if (!rowLocked) setOpenProductSearchIndex(index);
                                    }}
                                    onChange={(event) => {
                                      if (!rowLocked) updateRow(index, { productId: '', productSearch: event.target.value });
                                    }}
                                    onBlur={() => {
                                      window.setTimeout(() => {
                                        setOpenProductSearchIndex((current) => (current === index ? null : current));
                                        setRows((current) =>
                                          current.map((entry, rowIndex) => {
                                            if (rowIndex !== index || entry.productId === '') return entry;
                                            const product = meta.products.find((candidate) => candidate.id === Number(entry.productId));
                                            return product ? { ...entry, productSearch: productOptionLabel(product) } : entry;
                                          })
                                        );
                                      }, 120);
                                    }}
                                    disabled={rowLocked}
                                    className="w-full"
                                  />
                                  {openProductSearchIndex === index && !rowLocked && (
                                    <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-auto rounded border border-gray-200 bg-white shadow-lg">
                                      {matchingProducts.map((product) => (
                                        <button
                                          key={product.id}
                                          type="button"
                                          onMouseDown={(event) => {
                                            event.preventDefault();
                                            handleProductSelect(index, product.id);
                                          }}
                                          className="block w-full px-3 py-2 text-left text-sm hover:bg-orange-50"
                                        >
                                          {productOptionLabel(product)}
                                        </button>
                                      ))}
                                      {matchingProducts.length === 0 && <div className="px-3 py-2 text-sm text-gray-500">Ingen träff</div>}
                                    </div>
                                  )}
                                </div>
                                {rowLocked && (
                                  <div className="mt-1 text-xs text-amber-700">
                                    Batchen är låst eftersom den redan har använts i produktionen.
                                  </div>
                                )}
                              </td>
                              <td className="px-2 py-1.5 min-w-[90px]">
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={row.qty}
                                  onKeyDown={(event) => {
                                    if (isPieceUom(selectedUomLabel) && (event.key === '.' || event.key === ',')) {
                                      event.preventDefault();
                                    }
                                  }}
                                  onChange={(event) => updateRow(index, { qty: event.target.value })}
                                  disabled={rowLocked}
                                  className="w-full"
                                />
                              </td>
                              <td className="px-2 py-1.5 min-w-[90px]">
                                <select
                                  value={row.uomItemId}
                                  onChange={(event) => updateRow(index, { uomItemId: event.target.value ? Number(event.target.value) : '' })}
                                  disabled={rowLocked}
                                  className="w-full"
                                >
                                  <option value="">-</option>
                                  {meta.uoms.map((uom) => (
                                    <option key={uom.id} value={uom.id}>
                                      {uom.label}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-2 py-1.5 min-w-[140px]">
                                <select
                                  value={row.siteId}
                                  onChange={(event) => updateRow(index, { siteId: event.target.value ? Number(event.target.value) : '' })}
                                  disabled={rowLocked}
                                  className="w-full"
                                >
                                  <option value="">Välj lagerplats</option>
                                  {meta.sites.map((site) => (
                                    <option key={site.id} value={site.id}>
                                      {site.name}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-2 py-1.5 min-w-[90px]">
                                {showBulkFields ? (
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    value={row.waterPercent}
                                    onChange={(event) => updateRow(index, { waterPercent: event.target.value })}
                                    disabled={rowLocked}
                                    className="w-full"
                                  />
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-2 py-1.5 min-w-[220px]">
                                <input
                                  value={row.notes}
                                  onChange={(event) => updateRow(index, { notes: event.target.value })}
                                  disabled={rowLocked}
                                  className="w-full"
                                />
                              </td>
                              <td className="px-2 py-1.5 min-w-[90px]">
                                <button
                                  type="button"
                                  onClick={() => setRows((current) => current.filter((_, rowIndex) => rowIndex !== index))}
                                  disabled={rowLocked}
                                  className="rounded border border-red-200 px-2 py-1 text-sm text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  Ta bort
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-600">Anteckningar</label>
                <textarea
                  value={form.notes ?? ''}
                  onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
                  className="min-h-[100px] w-full"
                />
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover disabled:opacity-50"
                >
                  {saving ? 'Sparar...' : 'Spara'}
                </button>
                <button type="button" onClick={closeForm} className="rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-100">
                  Avbryt
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

